import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Symbol } from "../GameComponents/Symbol";
import { ITweenable, Tweens } from "../Components/Effects/Tweens";

/**
 * ReelExtended handles:
 *
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ReelExtended extends Reel {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    public static TeaseingSymbols: Array<number> = [];

    protected previousReel: ReelExtended;

    protected nextReel: ReelExtended;

    public stopped: boolean = false;

    //******************************************************************************************************************
    //  OVERRIDES
    //******************************************************************************************************************

    public showSymbols(useTransform: boolean = false): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];

        this.x = this.positionX;
        this.y = this.positionY;

        if (!useTransform) {
            while (this.currentSymbols.length > 0) {
                this.returnSymbol(this.currentSymbols.pop());
            }
        }

        let bottomSymbols: Array<Symbol> = [];
        let topSymbols: Array<Symbol> = [];

        for (let i = 0; i < this.symbolBottomCount + this.symbolCount + this.symbolTopCount; i++) {
            let symbolId: number;
            let symbolCase: string;

            if (i < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (i < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - i - 1];
                symbolCase = Symbol.CaseRotation;
            } else {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount + this.symbolCount ? Symbol.CaseRotation : Symbol.CaseBlur;
            }

            if (!useTransform) {
                // when we don't want use symbols from WinAnimator
                this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
            } else {
                // currentSymbols are used from WinAnimator. We only need to add new top and bottom symbols
                if (i < this.symbolBottomCount) {
                    bottomSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
                } else if (i > this.symbolBottomCount + this.symbolCount - 1) {
                    topSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
                }
            }
        }

        if (useTransform) {
            while (bottomSymbols.length > 0) {
                this.currentSymbols.push(bottomSymbols.shift());
            }

            while (topSymbols.length > 0) {
                this.currentSymbols.unshift(topSymbols.shift());
            }
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            if (this.currentSymbols[i] == null) {
                continue; //skip null symbol
            }

            this.addChild(this.currentSymbols[i]);
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }
        this.moveToTop();
    }

    public spinStart(): void {
        super.spinStart();
        this.stopped = false;
    }

    public spinFakeTease(): void {
        this.isTeasing = true;
    }

    protected spinPreRoll(): void {
        // super.spinPreRoll();
        Tweens.removeTweens(this, "position");
        let tween = Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY - this.preRoll, this.positionY],
            tweenDuration: this.preRollSpeed * 2,
            tweenDelay: this.startDelay,
            callbackFunction: this.spinRoll,
            callbackScope: this,
        });
    }

    protected spinRoll(): void {
        // super.spinRoll();
        Tweens.removeTweens(this, "position");
        let tween = Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + this.symbolHeight],
            tweenDuration: this.rollSpeed,
            callbackFunction: this.updateReel,
            callbackScope: this,
        });
    }

    protected spinPostRoll(): void {
        // super.spinPostRoll();
        Tweens.removeTweens(this, "position");
        let tween = Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + this.postRoll, this.positionY],
            tweenDuration: this.postRollSpeed * 2,
            callbackFunction: this.spinComplete,
            callbackScope: this,
        });
    }

    protected updateReel(): void {
        this.returnSymbol(this.currentSymbols.pop());

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if (this.previousReel.isStopping) {
                this.isStopping = true;
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = Symbol.CaseBlur;

        if (this.isStopping) {
            if (this.stoppingCounter < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                symbolCase = Symbol.CaseRotation;
            } else {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? Symbol.CaseRotation
                        : Symbol.CaseBlur;
            }

            this.stoppingCounter++;
        }

        this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));

        this.addChild(this.currentSymbols[0]);

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
        this.moveToTop();
    }

    protected spinComplete(): void {
        super.spinComplete();
        this.stopped = true;
    }

    protected getSymbol(symbolId: number, symbolCase: string): Symbol {
        if (symbolCase === Symbol.CaseRotation) {
            symbolCase = Symbol.CaseNormal;
        }

        let alpha = 1;
        if (this.isTeasing && this.teasingSymbols.indexOf(symbolId + 1) === -1) {
            symbolCase = Symbol.CaseNowin;
            alpha = 0.4;
        }

        const symbol = super.getSymbol(symbolId, symbolCase);
        symbol.alpha = alpha;

        return symbol;
    }

    protected returnSymbol(symbol: Symbol): void {
        symbol.alpha = 1;
        super.returnSymbol(symbol);
    }

    protected moveToTop(): void {
        //not implemented
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ReelExtended.ts
 **/